import type { MeshJob, MeshResult } from './types.ts';

/**
 * Pool of meshing workers.
 *
 * Jobs carry their own padded copy of the chunk, so workers hold no world state
 * and any idle worker can take any job. Jobs are dispatched nearest-first and a
 * chunk that is re-queued while a job is already in flight simply supersedes it
 * — the stale result is dropped on arrival by comparing revisions.
 */
export class MesherPool {
  private readonly workers: Worker[] = [];
  private readonly busy: boolean[] = [];
  private readonly queue: MeshJob[] = [];
  private readonly queued = new Set<number>();
  private onResult: (result: MeshResult) => void = () => {};
  /** Rolling average mesh time, milliseconds. */
  averageMs = 0;
  completed = 0;

  constructor(size = Math.max(1, Math.min(4, (navigator.hardwareConcurrency ?? 4) - 1))) {
    for (let i = 0; i < size; i++) {
      const worker = new Worker(new URL('./mesher.worker.ts', import.meta.url), { type: 'module' });
      const index = i;
      worker.onmessage = (ev: MessageEvent<MeshResult>) => {
        this.busy[index] = false;
        this.averageMs = this.averageMs * 0.9 + ev.data.ms * 0.1;
        this.completed++;
        this.onResult(ev.data);
        this.pump();
      };
      worker.onerror = (err) => {
        console.error('mesher worker error', err.message, err.filename, err.lineno);
        this.busy[index] = false;
        this.pump();
      };
      this.workers.push(worker);
      this.busy.push(false);
    }
  }

  get size(): number {
    return this.workers.length;
  }

  get pending(): number {
    return this.queue.length;
  }

  get inFlight(): number {
    return this.busy.filter(Boolean).length;
  }

  setHandler(fn: (result: MeshResult) => void): void {
    this.onResult = fn;
  }

  /** Queue a job, replacing any pending job for the same chunk. */
  submit(job: MeshJob): void {
    const key = job.cx * 0x100000 + job.cz;
    if (this.queued.has(key)) {
      const index = this.queue.findIndex((j) => j.cx === job.cx && j.cz === job.cz);
      if (index >= 0) this.queue[index] = job;
      else this.queue.push(job);
    } else {
      this.queued.add(key);
      this.queue.push(job);
    }
    this.pump();
  }

  /** Reorder the queue so the chunks nearest the camera are meshed first. */
  prioritise(cx: number, cz: number): void {
    if (this.queue.length < 2) return;
    this.queue.sort(
      (a, b) =>
        (a.cx - cx) ** 2 + (a.cz - cz) ** 2 - ((b.cx - cx) ** 2 + (b.cz - cz) ** 2),
    );
  }

  private pump(): void {
    for (let i = 0; i < this.workers.length && this.queue.length > 0; i++) {
      if (this.busy[i]) continue;
      const job = this.queue.shift()!;
      this.queued.delete(job.cx * 0x100000 + job.cz);
      this.busy[i] = true;
      this.workers[i]!.postMessage(job, [
        job.blocks.buffer,
        job.skyLight.buffer,
        job.blockLight.buffer,
        job.biomes.buffer,
      ]);
    }
  }

  clear(): void {
    this.queue.length = 0;
    this.queued.clear();
  }

  dispose(): void {
    for (const w of this.workers) w.terminate();
    this.workers.length = 0;
  }
}
